<script setup>
import AppLayout from "@/Layouts/AppLayout.vue";
import FormSection from "@/Components/FormSection.vue";
import PrimaryButton from "@/Components/PrimaryButton.vue";
import SecondaryButton from "@/Components/SecondaryButton.vue";
import { useForm } from "@inertiajs/inertia-vue3";
import { computed } from "vue";

const props = defineProps({
  tenant: { type: Object, required: true },
  pageVisibility: { type: Object, default: () => ({}) },
  pageVisibilityFields: { type: Array, default: () => [] },
});

const groupedPageVisibilityFields = computed(() => {
  return props.pageVisibilityFields.reduce((groups, field) => {
    const groupName = field.group || "Menu";

    if (!groups[groupName]) {
      groups[groupName] = [];
    }

    groups[groupName].push(field);

    return groups;
  }, {});
});

const form = useForm({
  _method: "PUT",
  page_visibility: { ...props.pageVisibility },
});

const saveForm = () => {
  form.post(route("admin.tenant-manager.navigation.update", { tenant: props.tenant.id }), {
    preserveScroll: true,
    onSuccess: () => {
      window.location.href = route("admin.tenant-manager.index");
    },
  });
};
</script>

<template>
  <AppLayout title="Tenant Navigation">
    <template #header>
      <h2 class="font-semibold text-xl text-gray-800 leading-tight">
        Navigation Visibility: {{ props.tenant.tenantName }}
      </h2>
    </template>

    <div class="max-w-3xl mx-auto py-10 sm:px-6 lg:px-8">
      <FormSection gridClasses="grid-cols-1" :noGrid="true">
        <template #title>
          <a :href="route('admin.tenant-manager.edit', { tenant: props.tenant.id })">
            <SecondaryButton>Back</SecondaryButton>
          </a>
        </template>

        <template #form>
          <div class="flex flex-col gap-4">
            <div
              v-for="(fields, groupName) in groupedPageVisibilityFields"
              :key="groupName"
              class="rounded border border-gray-200 overflow-hidden"
            >
              <div class="bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700">
                {{ groupName }}
              </div>
              <div class="divide-y divide-gray-200">
                <div
                  v-for="field in fields"
                  :key="field.key"
                  class="flex items-center justify-between gap-4 px-4 py-3"
                >
                  <div class="text-sm font-medium text-gray-900">
                    {{ field.label }}
                  </div>

                  <label class="inline-flex items-center cursor-pointer">
                    <input
                      v-model="form.page_visibility[field.key]"
                      type="checkbox"
                      class="sr-only peer"
                    />
                    <div
                      class="relative w-11 h-6 bg-gray-300 rounded-full transition-colors
                      peer-focus:outline-none peer-focus:ring-1 peer-focus:ring-green-400
                      peer-checked:bg-green-500
                      after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full
                      after:transition-all after:left-0.5 after:top-0.5
                      peer-checked:after:translate-x-5"
                    ></div>
                    <span class="ml-3 text-sm text-gray-700">
                      {{ form.page_visibility[field.key] ? "Visible" : "Hidden" }}
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div v-if="form.errors.page_visibility" class="text-sm text-red-600">
              {{ form.errors.page_visibility }}
            </div>
          </div>
        </template>

        <template #actions>
          <PrimaryButton @click.prevent="saveForm">
            Save
          </PrimaryButton>
        </template>
      </FormSection>
    </div>
  </AppLayout>
</template>
